import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { governmentServiceReducer } from "../../../state/reducers";
import { IGovernmentService } from "../../../utils/types/models";

const GovernmentServiceEditor: React.FC<IAddEntityScreenProps<IGovernmentService> | IEditEntityScreenProps<IGovernmentService>> = (
	props: IAddEntityScreenProps<IGovernmentService> | IEditEntityScreenProps<IGovernmentService>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(governmentServiceReducer, getInitialState<IGovernmentService>());
	const initialGovernmentService: IGovernmentService = {
		id: (props as IEditEntityScreenProps<IGovernmentService>).recordId || 0,
		name: "",
		active: true
	};
	const [governmentService, setGovernmentService] = useState<IGovernmentService>(initialGovernmentService);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setGovernmentService(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setGovernmentService(handleChange(governmentService, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(governmentService.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IGovernmentService): void {
		if (record && !close) {
			setGovernmentService(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IGovernmentService>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={governmentService}
			endpoint={Endpoint.GovernmentServices}
			entityState={state}
			entityType={t("governmentService")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={governmentService.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={governmentService.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={governmentService.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default GovernmentServiceEditor;
